import { useMemo } from 'react'
import type { FC } from 'react'
import type { Document } from '@contentful/rich-text-types'
import type { Options as RichTextRenderOptions } from '@contentful/rich-text-react-renderer'
import { BLOCKS, MARKS } from '@contentful/rich-text-types'
import styled from '@emotion/styled'

import Typography from '@segment/matcha/components/Typography'

import RichText from '@segment/matcha/recipes/RichText'

import type { TableOfContentState } from '@segment/utils/useRichTextTableOfContent'

import { scrollToAnchor } from '@segment/utils/useScrollToAnchor'

import Form from '../Form/Form'

import PropCardCta from './PropCardCta'

interface ContentSectionProps {
  data: Document
  tableOfContentState: TableOfContentState
}

const Content: FC<ContentSectionProps> = ({ data, tableOfContentState }) => {
  const options = useMemo<RichTextRenderOptions>(
    () => ({
      renderMark: {
        [MARKS.BOLD]: (text) => <Bold>{text}</Bold>,
      },
      renderNode: {
        [BLOCKS.PARAGRAPH]: (_, children) => (
          <StyledTypograpy variant="pLarge" tag="p">
            {children}
          </StyledTypograpy>
        ),
        [BLOCKS.QUOTE]: (_, children) => <Blockquote>{children}</Blockquote>,
        [BLOCKS.EMBEDDED_ENTRY](node) {
          switch (node.data.target.sys.contentType.sys.id) {
            case `inlineCta`: {
              const { heading, paragraph, buttonText, buttonUrl, image } = node.data.target.fields

              return (
                <PropCardCta
                  heading={heading}
                  paragraph={paragraph}
                  buttonText={buttonText}
                  buttonUrl={buttonUrl}
                  image={image}
                />
              )
            }

            default:
              return null
          }
        },
        [BLOCKS.HEADING_2](node, children) {
          const id = tableOfContentState.getId(node)
          return (
            <Typography variant="h2" tag="h2" id={id}>
              {children}
            </Typography>
          )
        },
      },
    }),
    [tableOfContentState]
  )

  const content = useMemo(() => {
    if (!data) {
      return
    }

    return <RichText data={data} options={options} />
  }, [data, options])

  const headingslist = data.content
    .filter((item) => item.nodeType === `heading-2`)
    .map((item) => (item.content[0] as { value: string }).value)

  const slugsList = headingslist.map((item) =>
    item
      .toLocaleLowerCase()
      .replace(/[^A-Za-z0-9 \s]/g, ``)
      .split(` `)
      .join(`-`)
  )

  const Toc = () => {
    return (
      <TocWrapper>
        <Typography variant="h2" tag="h2">
          Table of Contents
        </Typography>
        <ListItem variant="pLarge" tag="div">
          <ol>
            {headingslist.map((heading, id) => (
              <li key={heading}>
                <a
                  href={`#` + slugsList[id]}
                  onClick={(e) => {
                    e.preventDefault()
                    scrollToAnchor(slugsList[id], -100)
                  }}
                >
                  {heading}
                </a>
              </li>
            ))}
          </ol>
        </ListItem>
      </TocWrapper>
    )
  }

  return (
    <Wrapper>
      {headingslist.length > 0 && <Toc />}
      {content}
      <Form />
    </Wrapper>
  )
}

export default Content

const Wrapper = styled.article`
  display: flex;
  flex-direction: column;
  padding-bottom: 60px;
`

const Blockquote = styled.blockquote`
  padding-left: 21px;
  border-left: ${({ theme }) => `3px solid ${theme.palette.twilio.gray[20]}`};
  margin: 24px 0px;

  p {
    color: ${({ theme }) => theme.palette.twilio.gray[60]};
    margin: 0;
  }
`

const StyledTypograpy = styled(Typography)`
  margin: 16px 0;
`

const TocWrapper = styled.div`
  margin-bottom: 24px;
`

const ListItem = styled(Typography)`
  margin: 0;
`

const Bold = styled.b`
  font-weight: 600;
`
